import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { setCurrentVideo } from 'actions/currentVideo';
import { useGetAuthNStatus } from 'hooks/auth/subscriptions';
import { getHighlightsRowData } from 'support/scoringHelper';
import { isRowWithScoring } from 'support/rowHelper';
import { useScoring } from './scoring';

import {
  VideoCtvRedux,
  KitchenSinkRowItem,
  KitcenSinkRowItemDataItem,
} from '../store-types';
import { PageType } from '../app-types';

const SPORTS = 'sport';
const LEAGUES = 'league';
const MARKETS = 'market';
const SHOWS = 'show';

export const selectRows = (
  state: VideoCtvRedux,
  page: string,
  type: PageType,
): KitchenSinkRowItem[] | undefined => {
  if (type === SPORTS) {
    return state.sports.data[page]?.rows;
  }
  if (type === LEAGUES) {
    return state.leagues.data[page]?.rows;
  }
  if (type === MARKETS) {
    return state.localMarkets.data[page]?.rows;
  }
  if (type === SHOWS) {
    return state.shows.data[page]?.rows;
  }
  return state.kitchenSink.data?.rows;
};

const getAutoPlayObject = (
  state: VideoCtvRedux,
  page: string,
  type: PageType,
): KitcenSinkRowItemDataItem | null | undefined => {
  if (type === SPORTS) {
    return state.sports.data[page]?.autoplayObject;
  }
  if (type === LEAGUES) {
    return state.leagues.data[page]?.autoplayObject;
  }
  if (type === MARKETS) {
    return state.localMarkets.data[page]?.autoplayObject;
  }
  if (type === SHOWS) {
    return state.shows.data[page]?.autoplayObject;
  }
  return null;
};

export const useVideoById = (
  id?: string | null,
  rowId?: string,
  isDiscoveryRowVideo = false,
): [KitcenSinkRowItemDataItem | null | undefined, KitchenSinkRowItem | null | undefined] => {
  const rowsData = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink?.data?.rows,
  );
  const liveRow = rowsData?.filter(row => row.slug === 'live');
  const useCurrentRow = useRowById(rowId);

  const desiredRow = useMemo(() => {
    if (isDiscoveryRowVideo) {
      return liveRow[0];
    }
    return useCurrentRow;
  }, [isDiscoveryRowVideo, liveRow, useCurrentRow]);

  const desiredVideo = useMemo(() => {
    if (!desiredRow) return null;

    const result = desiredRow.data.find(item => item.id === id);
    return result;
  }, [id, desiredRow]);

  const page = useSelector((state: VideoCtvRedux) => state.pageView?.page);
  const type = useSelector((state: VideoCtvRedux) => state.pageView?.type) as PageType;

  const fallbackVideo = useSelector((state: VideoCtvRedux) => {
    const rows = selectRows(state, page, type);
    const heroData = state.kitchenSink.data?.hero?.data;
    const autoPlayObject = getAutoPlayObject(state, page, type);

    return (
      rows
        ?.map(item => item.data)
        .flat()
        .find(item => item?.id === id) ||
      heroData?.find(item => item?.id === id) ||
      autoPlayObject
    );
  });
  const fallbackRow = useRowByVideoId(id);

  return desiredVideo
    ? [desiredVideo, desiredRow]
    : [fallbackVideo, fallbackRow];
};

export const useRowById = (
  id: string,
  isDiscoveryRowVideo = false,
): KitchenSinkRowItem | null | undefined => {
  let page = useSelector((state: VideoCtvRedux) => state.pageView?.page);
  let type = useSelector((state: VideoCtvRedux) => state.pageView?.type) as PageType;

  if (isDiscoveryRowVideo) {
    page = 'home';
    type = null;
  }

  return useSelector((state: VideoCtvRedux) => {
    const rows = selectRows(state, page, type);
    if (!rows) return null;
    return rows.find(row => row.id === id);
  });
};

export const useRowByVideoId = (
  videoId: string,
): KitchenSinkRowItem | null | undefined => {
  const page = useSelector((state: VideoCtvRedux) => state.pageView?.page);
  const type = useSelector((state: VideoCtvRedux) => state.pageView?.type) as PageType;

  return useSelector((state: VideoCtvRedux) => {
    const rows = selectRows(state, page, type);
    if (!rows) return null;
    return rows.find(
      row => row.data?.findIndex(item => item.id === videoId) >= 0,
    );
  });
};

const useAutoplayVideo = () => {
  const page = useSelector((state: VideoCtvRedux) => state.pageView?.page);
  const type = useSelector((state: VideoCtvRedux) => state.pageView?.type) as PageType;

  const videoId = useSelector((state: VideoCtvRedux) => {
    if (type === SPORTS) {
      return state.sports.data[page]?.autoplay;
    }
    if (type === LEAGUES) {
      return state.leagues.data[page]?.autoplay;
    }
    if (type === MARKETS) {
      return state.localMarkets.data[page]?.autoplay;
    }
    if (type === SHOWS) {
      return state.shows.data[page]?.autoplay;
    }
    return state.kitchenSink.data?.autoplay;
  });

  const video = useSelector((state: VideoCtvRedux) => {
    if (type === SPORTS) {
      return state.sports.data[page]?.autoplayObject;
    }
    if (type === LEAGUES) {
      return state.leagues.data[page]?.autoplayObject;
    }
    if (type === MARKETS) {
      return state.localMarkets.data[page]?.autoplayObject;
    }
    if (type === SHOWS) {
      return state.shows.data[page]?.autoplayObject;
    }
    return state.kitchenSink.data?.autoplayObject;
  });

  const row = useRowByVideoId(videoId);

  return [video, row];
};

const useFirstVideo = () => {
  const page = useSelector((state: VideoCtvRedux) => state.pageView?.page);
  const type = useSelector((state: VideoCtvRedux) => state.pageView?.type) as PageType;

  const video = useSelector((state: VideoCtvRedux) => {
    let rows;
    if (type === SPORTS) {
      rows = state.sports.data[page]?.rows;
    } else if (type === LEAGUES) {
      rows = state.leagues.data[page]?.rows;
    } else if (type === MARKETS) {
      rows = state.localMarkets.data[page]?.rows;
    } else if (type === SHOWS) {
      rows = state.shows.data[page]?.rows;
    } else {
      rows = state.kitchenSink.data?.rows;
    }

    if (rows?.length > 0) {
      const { data: rowData } = rows[0];
      if (rowData && rowData.length) {
        return rowData[0];
      }
    }
    return null;
  });
  const row = useRowByVideoId(video?.id);
  return [video, row];
};

export const useFallbackVideo = () => {
  const [featuredVideo, featuredRow] = useAutoplayVideo();
  const [firstVideo, firstRow] = useFirstVideo();

  if (featuredVideo) return [featuredVideo, featuredRow];
  if (firstVideo) return [firstVideo, firstRow];

  return [null, null];
};

export const useCurrentVideoSetter = () => {
  const dispatch = useDispatch();

  return useCallback(
    (video, videoId, rowId, isDiscoveryRowVideo) => {
      dispatch(setCurrentVideo(video, videoId, rowId, isDiscoveryRowVideo));
    },
    [dispatch],
  );
};

export const useCurrentVideoIds = (): [string, string, boolean | undefined] => {
  const currentVideoId = useSelector(
    (state: VideoCtvRedux) => state.currentVideo.videoId,
  );
  const currentRowId = useSelector(
    (state: VideoCtvRedux) => state.currentVideo.rowId,
  );
  const isDiscoveryRowVideo = useSelector(
    (state: VideoCtvRedux) => state.currentVideo.isDiscoveryRowVideo,
  );
  return [currentVideoId, currentRowId, isDiscoveryRowVideo];
};

export const useCurrentVideo = () => {
  const [currentVideoId, rowId, isDiscoveryRowVideo] = useCurrentVideoIds();
  const [currentVideo, currentRow] = useVideoById(
    currentVideoId,
    rowId,
    isDiscoveryRowVideo,
  );

  // TODO: integrate video aggregate and remove last played video and row
  const lastPlayedVideo = useSelector(
    (state: VideoCtvRedux) => state.currentVideo.video,
  );
  const lastPlayedRow = useRowById(rowId, isDiscoveryRowVideo);

  return currentVideo
    ? [currentVideo, currentRow, isDiscoveryRowVideo] as const
    : [lastPlayedVideo, lastPlayedRow, undefined] as const;
};

const usePrevOrNextVideo = (isNext = true) => {
  const [video, row] = useCurrentVideo();
  const [fallbackVideo, fallbackRow] = useFallbackVideo();
  const getAuthNStatus = useGetAuthNStatus();
  const [games, scoringError] = useScoring();

  // @ts-ignore - video type union includes types without event property
  const [postEventVideo, postEventRow] = useVideoById(video?.event?.postEvent);

  if (video) {
    // @ts-ignore - video type union includes types without event property
    if (video?.event?.postEvent) {
      return [postEventVideo, postEventRow];
    }
    if (row) {
      let updatedRow = null;
      if (row && isRowWithScoring(row) && !scoringError && games) {
        const highlightsRowData = getHighlightsRowData(row, games);
        if (highlightsRowData.length > 0) {
          updatedRow = [...highlightsRowData];
        }
      }
      const rowVideos = updatedRow || [...row.data];

      // Want to work backwards if we're ever on the DVR row, or if we're looking
      // for the previous video.  NOTE: If we're on both a DVR row and looking
      // for the previous video, we should be reversing twice (back to the original
      // array)
      if (row.type === 'dvr') {
        rowVideos.reverse();
      }
      if (!isNext) {
        rowVideos.reverse();
      }
      const currentVideoIndex = rowVideos.findIndex(
        item => item.id === video.id,
      );

      if (currentVideoIndex > -1) {
        const newIndex = rowVideos.findIndex((item, index) => {
          if (index <= currentVideoIndex) return false;
          return getAuthNStatus(item.authTypes);
        });
        const newVideo =
          newIndex < rowVideos.length && newIndex >= 0
            ? rowVideos[newIndex]
            : null;

        if (newVideo) return [newVideo, row];
      }
    }
  }

  if (isNext) {
    if (fallbackVideo) return [fallbackVideo, fallbackRow];
  }

  return [null, null];
};

export const usePrevVideo = () => {
  return usePrevOrNextVideo(false);
};

export const useNextVideo = () => {
  return usePrevOrNextVideo(true);
};
